/**
 * The way in, when the app does not know whose it is.
 *
 * A password of the shape they use at school — a word and a number.
 *
 * The link still works and is still the quick way. What the field is for is
 * the case where it does not: a link that arrives by messenger opens in that
 * app's own browser, and the password then sits in a window that is not the
 * one the child writes in.
 *
 * Upper and lower case do not matter — see `keyFromText`. A phone
 * capitalises the first letter by itself, and a capitalised password being refused would
 * be the app's fault.
 *
 * It is checked against the server BEFORE it is stored. Otherwise a typo
 * could not be told from a real password — the app would take it, say hello,
 * and fail quietly at the next sync two minutes later.
 */

import { useSignal } from '@preact/signals'
import { useRef } from 'preact/hooks'
import { signIn } from '../editor/sync.js'
import { Icon, Logo } from './icons.js'

export function SignIn({ onDone }: { onDone: (name: string) => void }) {
  const value = useSignal('')
  const busy = useSignal(false)
  /** What went wrong — shown as a sentence, not as a code. */
  const problem = useSignal('')
  const field = useRef<HTMLInputElement>(null)

  const submit = async (e: Event): Promise<void> => {
    e.preventDefault()
    if (busy.value) return
    const text = value.value.trim()
    if (!text) {
      problem.value = 'Da steht noch nichts.'
      field.current?.focus()
      return
    }

    busy.value = true
    problem.value = ''
    try {
      const name = await signIn(text)
      if (name === null) {
        // Both cases read the same on screen — for whoever is standing in
        // front of it, a mistyped key and a withdrawn one are one problem:
        // this key does not work, fetch the right link.
        problem.value = 'Das Passwort stimmt nicht. Schau noch mal nach.'
        field.current?.select()
        return
      }
      onDone(name)
    } catch {
      // A dead network is NOT a wrong key, and saying so would send the child
      // hunting for a link that is perfectly fine.
      problem.value = 'Keine Verbindung. Probier es gleich noch einmal.'
    } finally {
      busy.value = false
    }
  }

  return (
    <div class="signin">
      <div class="signin-card">
        {/* The mark gets room here, and it needs it: measured at five sizes,
            the scribble only reads as a scribble from about 48px up. Below
            that the strokes merge and it turns into a hatched block. This is
            the one screen with space for it. */}
        <p class="signin-logo">
          <Logo size={56} />
          <span>Kritzl</span>
        </p>
        <h1 class="signin-head">Schön, dass du da bist</h1>
        <p class="signin-text">
          Tipp dein Passwort ein, dann findet Kritzl deine Texte.
        </p>

        <form class="signin-form" onSubmit={submit}>
          <label class="signin-label" for="signin-key">Dein Passwort</label>
          <input
            ref={field}
            id="signin-key"
            class={`signin-input${problem.value ? ' bad' : ''}`}
            type="text"
            value={value.value}
            onInput={(e) => {
              value.value = (e.currentTarget as HTMLInputElement).value
              // The complaint goes away as soon as something changes: it was
              // about what stood there before.
              if (problem.value) problem.value = ''
            }}
            autocomplete="off"
            autocapitalize="off"
            autocorrect="off"
            spellcheck={false}
            enterkeyhint="go"
            aria-describedby={problem.value ? 'signin-problem' : undefined}
            aria-invalid={problem.value ? 'true' : undefined}
            disabled={busy.value}
          />

          {/* `role="alert"` so a screen reader says it — the field is where
              the eye already is, but not every reader is an eye. */}
          {problem.value && (
            <p id="signin-problem" class="signin-problem" role="alert">
              {problem.value}
            </p>
          )}

          <button class="action primary signin-go" type="submit" disabled={busy.value}>
            {busy.value ? 'Einen Augenblick …' : 'Los geht’s'}
            {!busy.value && <Icon name="arrow-right" size={18} />}
          </button>
        </form>

        <p class="signin-aside">
          Dein Passwort hat dir jemand gegeben, der Kritzl für dich
          eingerichtet hat. Groß oder klein ist egal — aber gib es nicht
          weiter.
        </p>
      </div>
    </div>
  )
}
